import { fetchAllPvSize } from './allPvSizeService';
import { DGSTATS_SOLAR_UPSTREAM_PRODUCT, DGSTATS_SOLAR_CAPACITY_BASIS } from './solarDgStatsLineage';
import { SOLAR_ANALYSIS_CUTOFF_YEAR, SOLAR_PUBLIC_TIME_SEMANTICS } from './solarAnalysisCutoff';
import type { SolarPerCapitaSnapshot } from './solarViewMetrics';
import {
  buildSolarCumulativeSectorTableFromRecords,
  type PvSizeSectorRecord,
} from './solarCumulativeSectorTable';

const SOURCE_PRODUCT = `California DGStats ${DGSTATS_SOLAR_UPSTREAM_PRODUCT}`;
const CAPACITY_BASIS = DGSTATS_SOLAR_CAPACITY_BASIS;

export type SolarInstallerExportRow = {
  installer: string;
  manufacturer: string;
  represented_capacity_kw_dc: number;
  represented_project_count: number;
  capacity_share_of_displayed_top10: number;
};

function metaRow(extra: Record<string, string | number>) {
  return {
    source_product: SOURCE_PRODUCT,
    capacity_basis: `System Size ${CAPACITY_BASIS}`,
    analysis_cutoff_year: SOLAR_ANALYSIS_CUTOFF_YEAR,
    time_semantics: SOLAR_PUBLIC_TIME_SEMANTICS,
    ...extra,
  };
}

export function buildSolarCapacityExportRows(
  location: string,
  chartData: { labels: (number | string)[]; datasets: { label: string; data: number[] }[] } | null | undefined
): Record<string, string | number>[] {
  if (!chartData?.labels?.length || !chartData.datasets?.length) return [];
  const sectorKeys = chartData.datasets.map((ds) => ds.label);
  return chartData.labels.map((label, i) => {
    const year = Number(label);
    const row: Record<string, string | number> = {
      location,
      approval_cohort_year: year,
      ...metaRow({}),
    };
    let total = 0;
    for (const ds of chartData.datasets) {
      const mw = Number(ds.data?.[i]);
      const val = Number.isFinite(mw) ? mw : 0;
      const key = `${String(ds.label).toLowerCase().replace(/\s+/g, '_')}_capacity_mw_dc`;
      row[key] = val;
      total += val;
    }
    row.total_capacity_mw_dc = total;
    if (!sectorKeys.length) row.note = 'no_sector_breakdown';
    return row;
  });
}

export async function buildSolarCumulativeExportRows(
  location: string,
  geofips: number
): Promise<Record<string, string | number>[]> {
  const { data, error } = await fetchAllPvSize({
    select: 'year, sector, total_watts, num_projects, geography',
    geofips,
  });
  if (error || !data?.length) return [];

  const tableRows = buildSolarCumulativeSectorTableFromRecords(data as PvSizeSectorRecord[]);
  if (!tableRows.length) return [];

  return tableRows.map((row) => ({
    location,
    ...row,
    ...metaRow({}),
  }));
}

export { buildSolarCumulativeSectorTableFromRecords } from './solarCumulativeSectorTable';

export function buildSolarPerCapitaExportRows(
  location: string,
  snapshot: SolarPerCapitaSnapshot | null
): Record<string, string | number>[] {
  if (
    !snapshot ||
    snapshot.latestCapacityKw == null ||
    snapshot.sameYearPopulation == null ||
    snapshot.localSolarKwPerResident == null ||
    snapshot.latestYear == null
  ) {
    return [];
  }
  return [
    {
      location,
      population_year: snapshot.latestYear,
      represented_capacity_mw_dc: snapshot.latestCapacityKw / 1000,
      population: snapshot.sameYearPopulation,
      capacity_kw_dc_per_resident: snapshot.localSolarKwPerResident,
      ...metaRow({
        time_semantics:
          `DC capacity of Project Sites with approval cohorts through ${SOLAR_ANALYSIS_CUTOFF_YEAR} divided by ${snapshot.latestYear} population`,
      }),
    },
  ];
}

export function buildSolarInstallerCapacityExportRows(
  location: string,
  rows: SolarInstallerExportRow[]
): Record<string, string | number>[] {
  if (!rows.length) return [];
  const totalKw = rows.reduce((s, r) => s + r.represented_capacity_kw_dc, 0);
  return rows.map((r) => ({
    location,
    installer: r.installer,
    manufacturer: r.manufacturer,
    represented_capacity_kw_dc: r.represented_capacity_kw_dc,
    represented_project_count: r.represented_project_count,
    top10_rollup: 'yes',
    capacity_share_of_displayed_top10:
      totalKw > 0 ? (r.represented_capacity_kw_dc / totalKw) * 100 : 0,
    source_product: SOURCE_PRODUCT,
    capacity_basis: `System Size ${CAPACITY_BASIS}`,
    universe: 'upstream top-10 installer-manufacturer pairs by represented installed capacity (DC kW)',
  }));
}

export function isSolarPublicExportView(
  pvView: string
): pvView is 'capacity' | 'cumulative' | 'perCapita' | 'installers' {
  return pvView === 'capacity' || pvView === 'cumulative' || pvView === 'perCapita' || pvView === 'installers';
}
